import type * as React from "react";

import type { IStyle, ITheme } from "@fluentui/react/lib/Styling";
import type {
  IRefObject,
  IStyleFunctionOrObject,
} from "@fluentui/react/lib/Utilities";

import type { AppResizeGroup } from "./AppResizeGroup";

export enum AppResizeGroupDirection {
  horizontal = 0,
  vertical = 1,
}

export interface ResizeGroup {
  /**
   * Remeasures the available space.
   */
  remeasure(): void;
}

export interface AppResizeGroupProps
  extends React.HTMLAttributes<AppResizeGroup | HTMLElement> {
  /**
   * Optional callback to access the ResizeGroup  interface. Use this instead of ref for accessing
   * the public methods and properties of the component.
   */
  componentRef?: IRefObject<ResizeGroup>;

  /**
   * Call to provide customized styling that will layer on top of the variant rules
   * @deprecated Removed to reduce bundle size.  Please use `className` and add css rules to `className` instead.
   */
  styles?: IStyleFunctionOrObject<
    AppResizeGroupStyleProps,
    AppResizeGroupStyles
  >;

  /**
   * Theme provided by HOC.
   */
  theme?: ITheme;

  /**
   * Additional css class to apply to the Component
   * @defaultvalue undefined
   */
  className?: string;

  /**
   * Direction of this resize group, vertical or horizontal
   * @defaultvalue AppResizeGroupDirection.horizontal
   */
  direction?: AppResizeGroupDirection;

  /**
   * Initial data to be passed to the onRenderData function. When there is no onGrowData provided, this data should represent what should
   * be passed to the render function when the parent container of the ResizeGroup is at it's maximum supported width. A cacheKey property
   * may optionally be included as part of the data. Two data objects with the same cacheKey will be assumed to take up the
   * same width and will prevent measurements. The type of cacheKey is a string.
   */
  data: any;

  /**
   * Function to render the data. Called when rendering the contents to the screen and when
   * rendering in a hidden div to measure the size of the contents.
   */
  onRenderData: (data: any) => JSX.Element;

  /**
   * Function to be performed on the data in order to reduce its width and make it fit into the given space.
   * If there are no more scaling steps to apply, it should return undefined to prevent
   * an infinite render loop. MeasuredDimension indicates the width of the content previously rendered by
   * "prevData"
   */
  onReduceData: (prevData: any, measuredDimension?: number) => any;

  /**
   * Function to be performed on the data in order to increase its width. It is called in scenarios where the
   * container has more room than the previous render and we may be able to fit more content. If there are no more
   * scaling operations to perform on teh data, it should return undefined to prevent an infinite render loop.
   */
  onGrowData?: (prevData: any) => any;

  /**
   * Function to be performed on the data when we know how many scalingSteps to apply in total
   * and want to batch all of them together instead of making multiple calls to onGrowData/onReduceData.
   *
   * To use this,
   * 1. Your data must always contain a property called `appliedScalingStepCount` which we will
   *    use to determine how many scalingSteps to apply when we next batch scale data.
   * 2. Your data must also contain `cacheKey` for this prop to be used.
   *
   * Correct usage of this prop will allow us to have optimized rendering. Because this will reduce multiple calls
   * to onGrowData and onReduceData in some cases you can better memoize your data and also avoid redundant computations.
   */
  onBatchScaleData?: (prevData: any, scalingStepsToApply: number) => any;

  /**
   * Function to be called every time data is rendered. It provides the data that was actually rendered.
   * A use case would be adding telemetry when a particular control is shown in an overflow well or
   * dropped as a result of onReduceData or to count the number of renders that an implementation of
   * onReduceData triggers.
   */
  dataDidRender?: (renderedData: any) => void;

  /**
   * Function that allows the parent to dictate additional scaling conditions. If isScalingComplete returns false,
   * the AppResizeGroup will continue to scale down even if the content fits inside the container
   */
  isScalingComplete?: (data: any) => boolean;

  /**
   * True if the data should be re-measured, even if the content has been previously cached
   */
  dataNeedsMeasuring?: boolean;

  /**
   * Called once scaling has completed and the final content has been rendered
   */
  onAfterScalingComplete?: (data: any) => void;

  /**
   * If true, the initial content will be rendered visibly while the measurements are performed and the scaling logic is applied
   */
  measureContentVisibly?: boolean;

  /**
   * Optional callback to be used to measure the content rendered by the AppResizeGroup
   */
  measureRenderedContent?: () => number;
}

export interface AppResizeGroupStyleProps {
  /**
   * Accept theme prop.
   */
  theme: ITheme;

  /**
   * Accept custom classNames
   */
  className?: string;
}

export interface AppResizeGroupStyles {
  /**
   * Style for the root element.
   */
  root: IStyle;
}
